import sha256 from 'crypto-js/sha256';
import Logger from 'wanderu-logger';

import { dataLayer } from './utils';

const logger = Logger.getLogger('GtmUserData');

const GMAIL_DOMAINS = ['gmail.com', 'googlemail.com'];
const MAX_EMAIL_LENGTH = 254;
const USER_DATA_READY_EVENT = 'user_data_ready';

/**
 * Structural check only, deliberately looser than the RFC 5322 pattern nexus and
 * canopy validate against. This module's contract is the *normalization* — get
 * that wrong and a valid address hashes to a digest their producers will not
 * match. Accepting an address they would reject only means we hash something
 * that matches nobody, which costs a wasted field rather than a wrong join.
 */
function isStructurallyEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= MAX_EMAIL_LENGTH;
}

/**
 * Google's enhanced-conversions email normalization: trim, lowercase, and drop
 * the dots in a gmail/googlemail local part.
 *
 * **This is a cross-repo contract, not a local choice.** canopy, nexus and
 * seo-data-transformer-pipeline's Google Ads offline upload apply the same rule,
 * and the same person reaches Google down more than one of those paths — a
 * digest that differs is a silently missed match, never an error. TikTok does
 * not strip dots; Google is the primary channel and a dotted-gmail visitor
 * matches TikTok by click id instead. Google's Ads API doc adds a second gmail
 * rule dropping `+` and what follows; it is deliberately **not** applied here,
 * because the pipeline's hasher does not apply it either and parity with our own
 * producers is worth more than parity with the API-side rule.
 */
function normalizeEmail(email) {
    const trimmed = email.trim().toLowerCase();
    if (!isStructurallyEmail(trimmed)) {
        return null;
    }
    const at = trimmed.lastIndexOf('@');
    const domain = trimmed.slice(at + 1);
    if (GMAIL_DOMAINS.indexOf(domain) === -1) {
        return trimmed;
    }
    // A local part that was only dots is nothing once stripped. canopy returns
    // null here too; hashing '@gmail.com' would send a digest that matches nobody.
    const local = trimmed.slice(0, at).split('.').join('');
    return local
        ? `${local}@${domain}`
        : null;
}

/**
 * Lowercase hex SHA-256 of the UTF-8 bytes (`hashlib.sha256(s.encode()).hexdigest()`).
 *
 * crypto-js rather than Web Crypto: it is already in the client vendor chunk via
 * wanderu-utils, it is synchronous, and it does not need a secure context —
 * `crypto.subtle` is undefined on the plain-http `local.wanderu.com` dev host.
 * Synchronous matters: the report lands in the caller's tick, so a caller that
 * does not await (most do not) still has the model set before its next event.
 */
function sha256Hex(value) {
    return sha256(value).toString();
}

function hashOrOmit(value) {
    const normalized = value
        ? value.trim().toLowerCase()
        : '';
    // Never hash '': a digest of the empty string matches nobody while looking
    // like a supplied value.
    return normalized
        ? sha256Hex(normalized)
        : undefined;
}

/**
 * Normalize per Google's rules and hash to the pre-hashed shape the `awec`
 * variable reads: `sha256_email_address` at the top level, the name under
 * `address`. A name placed at the top level is not an error to that variable, it
 * is simply not read.
 *
 * Pre-hashed because our tag points at the server container: in that mode
 * `gtag.js` does not hash in the browser, so a plaintext key travels to sGTM as
 * itself and lands in its preview output and ingress logs.
 *
 * Returns `null` without a usable email — the caller reports nothing.
 */
function normalizeAndHashUserData({ email, firstName, lastName }) {
    const normalizedEmail = email
        ? normalizeEmail(email)
        : null;
    if (!normalizedEmail) {
        return null;
    }

    const sha256FirstName = hashOrOmit(firstName);
    const sha256LastName = hashOrOmit(lastName);

    const address = {};
    if (sha256FirstName) address.sha256_first_name = sha256FirstName;
    if (sha256LastName) address.sha256_last_name = sha256LastName;

    const userData = { sha256_email_address: sha256Hex(normalizedEmail) };
    if (Object.keys(address).length > 0) userData.address = address;
    return userData;
}

// Serialized form of the last identity pushed, or null once retracted. The
// model already holds it, so pushing it again would only produce a duplicate
// `user_data_ready` hit — and Checkout re-identifies on every field blur.
let lastPushed = null;

/**
 * Retract the identity. The tags read the accumulated data model, not the push,
 * so until something clears the key the last-reported visitor keeps being sent —
 * on every ordinary page event, not only at a conversion. Called on logout by
 * wanderu-ducks-account.
 */
function clearGtmUserData() {
    lastPushed = null;
    dataLayer.push({ user_data: null });
}

/**
 * Clear, set, then announce — unless the model already holds exactly this.
 *
 * **Clearing first is what makes this a replace.** The model merges an object
 * push field by field, so a report carrying no name would otherwise inherit the
 * previous one — pairing one visitor's email with another's hashed name. This
 * app has two producers that can describe *different people*: the logged-in
 * identify, and an email signup whose address need not be the session's. canopy
 * deliberately does the opposite, because its two producers are complementary
 * halves of one order's identity; do not port either rule to the other.
 *
 * **The announce is what produces a hit.** Setting the key reaches nothing on
 * its own — tags read the model when an event is processed. A container tag
 * fires on `user_data_ready` and carries the key. The retraction above
 * deliberately does not announce: a hit whose `em` is present but empty is
 * Google's documented signature for "user-provided data was not available".
 */
function pushGtmUserData(userData) {
    const serialized = JSON.stringify(userData);
    if (serialized === lastPushed) {
        return;
    }
    clearGtmUserData();
    dataLayer.push({ user_data: userData });
    dataLayer.push({ event: USER_DATA_READY_EVENT });
    lastPushed = serialized;
}

/**
 * Hash and report, or do nothing. Swallows any failure, because losing a match
 * signal must never take the caller's own event with it.
 */
function reportGtmUserData(input) {
    try {
        const userData = normalizeAndHashUserData(input);
        if (userData) pushGtmUserData(userData);
    } catch (ex) {
        logger.error('reportGtmUserData', ex);
    }
}

export {
    USER_DATA_READY_EVENT,
    clearGtmUserData,
    normalizeAndHashUserData,
    pushGtmUserData,
    reportGtmUserData,
    sha256Hex,
};
